/** Rótulos e utilitários da Fase 3 (importação, atividades, comparação). */
import type { ActivitySport, Comparison, ImportJob, ImportStatus, PaceResult } from "../api/types";
import { formatDuration, formatPace, metersToKm } from "./format";
import { LOCALE, t, tn } from "../i18n";

export const SPORT_LABEL: Record<ActivitySport, string> = {
  run: t("Corrida"),
  trail_run: t("Trail"),
  treadmill: t("Esteira"),
  walk: t("Caminhada"),
  bike: t("Bike"),
  other: t("Outra"),
};

export const IMPORT_STATUS: Record<ImportStatus, { label: string; tone: "ok" | "warn" | "off" | "danger" }> = {
  queued: { label: t("Na fila"), tone: "off" },
  processing: { label: t("Processando"), tone: "off" },
  succeeded: { label: t("Importado"), tone: "ok" },
  partial: { label: t("Importado em parte"), tone: "warn" },
  duplicate: { label: t("Já existia"), tone: "warn" },
  needs_review: { label: t("Aguardando revisão"), tone: "warn" },
  applied: { label: t("Aplicado na planilha"), tone: "ok" },
  failed: { label: t("Falhou"), tone: "danger" },
};

export const PACE_RESULT: Record<PaceResult, { label: string; tone: "ok" | "warn" | "off" | "danger" }> = {
  on_target: { label: t("No alvo"), tone: "ok" },
  faster: { label: t("Mais rápido"), tone: "warn" },
  slower: { label: t("Mais lento"), tone: "danger" },
  no_target: { label: t("Sem alvo"), tone: "off" },
  no_data: { label: t("Sem dados"), tone: "off" },
};

export const TOTALS_STATUS: Record<Comparison["totals"]["status"], { label: string; tone: "ok" | "warn" | "off" | "danger" }> = {
  completed: { label: t("Volume cumprido"), tone: "ok" },
  partial: { label: t("Volume parcial"), tone: "danger" },
  above: { label: t("Acima do planejado"), tone: "warn" },
  no_reference: { label: t("Sem volume planejado"), tone: "off" },
};

export const ACCEPTED_ACTIVITY_FILES = ".fit,.tcx,.gpx,.csv,.xlsx,.gz,.zip";

export const isPending = (j: ImportJob) => j.status === "queued" || j.status === "processing";

/** Uma linha que explica o resultado de um job para quem enviou. */
export function jobSummary(j: ImportJob): string {
  if (j.status === "failed") return j.error_message ?? t("Falha ao ler o arquivo.");
  if (j.status === "duplicate") return j.result.message ?? t("Este conteúdo já tinha sido importado.");
  if (j.status === "needs_review") return tn("{n} dia reconhecido. Revise antes de aplicar.", "{n} dias reconhecidos. Revise antes de aplicar.", j.result.proposal?.items.length ?? 0);
  if (j.status === "applied") return tn("{n} treino criado como rascunho.", "{n} treinos criados como rascunho.", j.result.applied?.sessions_created ?? 0);
  if (j.kind === "activity_table" && (j.status === "succeeded" || j.status === "partial")) {
    const r = j.result;
    const parts = [tn("{n} atividade criada", "{n} atividades criadas", r.created ?? 0)];
    if (r.duplicates) parts.push(tn("{n} já existia", "{n} já existiam", r.duplicates));
    if (r.rejected?.length) parts.push(tn("{n} linha recusada", "{n} linhas recusadas", r.rejected.length));
    if (r.matched) parts.push(tn("{n} vinculada a treino", "{n} vinculadas a treinos", r.matched));
    return parts.join(", ") + ".";
  }
  if (j.status === "succeeded") return j.result.matched_workout_id ? t("Atividade vinculada ao treino do dia.") : t("Atividade importada sem treino correspondente.");
  return t("Aguardando processamento.");
}

export function pct(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return `${v > 0 ? "+" : ""}${v.toLocaleString(LOCALE, { maximumFractionDigits: 1 })}%`;
}

export function paceRange(min: number | null, max: number | null): string {
  if (!min && !max) return "—";
  if (min && max && min !== max) return `${formatPace(min).replace(" /km", "")}–${formatPace(max)}`;
  return formatPace(min ?? max);
}

export function plannedVolume(distance: number | null, duration: number | null): string {
  if (distance) return metersToKm(distance);
  if (duration) return formatDuration(duration);
  return t("Livre");
}

export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toLocaleString(LOCALE, { maximumFractionDigits: 0 })} KB`;
  return `${(bytes / 1024 / 1024).toLocaleString(LOCALE, { maximumFractionDigits: 1 })} MB`;
}

export function localTime(isoDateTime: string, tz: string): string {
  return new Intl.DateTimeFormat(LOCALE, { timeZone: tz, hour: "2-digit", minute: "2-digit" }).format(new Date(isoDateTime));
}
